import { useState } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';

const TIERS = {
  basic: {
    label: '🥉 Basic Boost',
    price: 5,
    days: 3,
    features: [
      'Featured in category section',
      'Highlighted badge',
      '+50% visibility'
    ]
  },
  premium: {
    label: '🥈 Premium Boost',
    price: 10,
    days: 7,
    recommended: true,
    features: [
      'Featured in category section',
      'Featured on homepage category',
      '2x visibility in trending',
      'View/click analytics'
    ]
  },
  vip: {
    label: '🥇 VIP Boost',
    price: 20,
    days: 7,
    features: [
      'Homepage "Promoted" carousel',
      '3x visibility boost',
      'Analytics dashboard',
      'Priority placement'
    ]
  }
};

export default function PromotionPurchase({ listing, isOpen, onClose, onPurchase }) {
  const [selectedTier, setSelectedTier] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  if (!isOpen || !listing) return null;

  const handlePurchase = async () => {
    if (!selectedTier) {
      toast.error('Select a tier');
      return;
    }

    setPurchasing(true);
    try {
      const promotion = await API.post(`/promotions`, {
        listingId: listing.id,
        tier: selectedTier
      });

      toast.success(`🎉 ${TIERS[selectedTier].label} activated!`);
      onPurchase?.(promotion.data.promotion);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          zIndex: 998,
          backdropFilter: 'blur(2px)'
        }}
        onClick={onClose}
      />

      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'white',
          borderRadius: 16,
          padding: 32,
          zIndex: 999,
          maxWidth: 640,
          width: 'calc(100% - 32px)',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxShadow: '0 20px 56px rgba(26,18,10,0.13)',
          animation: 'scaleIn 0.3s ease'
        }}
      >
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
            Promote Your Listing
          </h2>
          <p style={{ fontSize: 13, color: 'var(--text-soft)' }}>
            {listing.title}
          </p>
        </div>

        <div style={{ display: 'grid', gap: 16, marginBottom: 28 }}>
          {Object.entries(TIERS).map(([tier, info]) => (
            <button
              key={tier}
              onClick={() => setSelectedTier(tier)}
              style={{
                padding: 20,
                textAlign: 'left',
                border: selectedTier === tier ? '2px solid var(--cherry)' : '1px solid var(--border)',
                background: selectedTier === tier ? 'var(--cherry-light)' : 'white',
                borderRadius: 12,
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative'
              }}
            >
              {info.recommended && (
                <div style={{
                  position: 'absolute',
                  top: -12,
                  right: 16,
                  background: 'var(--cherry)',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: 20,
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase'
                }}>
                  RECOMMENDED
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                    {info.label}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-soft)' }}>
                    {info.days} days
                  </div>
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--cherry)' }}>
                  ${info.price}
                </div>
              </div>

              <div style={{ fontSize: 12 }}>
                {info.features.map((f, i) => (
                  <div key={i} style={{ marginBottom: 6, color: 'var(--text-mid)' }}>
                    ✓ {f}
                  </div>
                ))}
              </div>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={handlePurchase}
            disabled={!selectedTier || purchasing}
            className="btn btn-cherry"
            style={{ flex: 1 }}
          >
            {purchasing ? '⏳ Processing...' : `💳 Pay $${selectedTier ? TIERS[selectedTier].price : '0'}`}
          </button>
          <button
            onClick={onClose}
            disabled={purchasing}
            className="btn btn-ghost"
            style={{ flex: 1 }}
          >
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
